'use client'

import Image from 'next/image'
import { Plus, Clock } from 'lucide-react'
import type { MenuItemView } from '@/lib/microsite/menuItemView'
import { useLowStock } from '@/components/menu/LowStockContext'
import { LowStockBadge } from '@/components/menu/LowStockBadge'

/**
 * Presentación por defecto de la carta: tarjetas con foto grande en rejilla.
 * Es la que la página tuvo siempre — se extrajo tal cual de MenuSection para
 * que puedan convivir otras presentaciones sin duplicar la lógica de dinero.
 *
 * 🔴 Recibe `MenuItemView` y NO el ítem crudo: el precio ya viene resuelto
 * (`effectiveItemCardPrice`) y acá no hay forma de pintar otro.
 */
export function MenuLayoutClassic({
  views,
  onTap,
  addLabel,
  soldOutLabel,
  emptyLabel,
  fromLabel,
  productHrefs,
}: {
  views: MenuItemView[]
  onTap: (id: string) => void
  addLabel: string
  /** Rótulo del 86 ("Agotado"): el plato se muestra apagado, no se oculta. */
  soldOutLabel: string
  emptyLabel: string
  /** «desde» de un producto con variantes de distinto precio (súper online, R6). */
  fromLabel?: string
  /** Súper online con `online_retail_v2`: itemId → link ya armado de la página del producto.
   *  Con link, la foto y el nombre abren la página y «Agregar» es un botón aparte (el mismo
   *  `onTap`). Sin link (todo plato, todo restaurante) la tarjeta es el `<button>` de siempre. */
  productHrefs?: Record<string, string>
}) {
  const lowStock = useLowStock()
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 mt-3 sm:mt-6">
      {views.map((v) => {
        // Agotado (86) manda sobre la ventana horaria: mismo apagado visual,
        // pero con badge "Agotado" en vez del reloj.
        const blocked = v.soldOut || !v.availableNow
        // «¡Quedan pocas!» solo sobre lo que todavía se puede pedir.
        const few = !blocked && lowStock.item(v.id)
        const href = productHrefs?.[v.id]
        if (href) {
          return (
            <ProductLinkCard
              key={v.id}
              v={v}
              href={href}
              blocked={blocked}
              few={few}
              onAdd={() => onTap(v.id)}
              addLabel={addLabel}
              soldOutLabel={soldOutLabel}
              fromLabel={fromLabel}
            />
          )
        }
        return (
        <button
          key={v.id}
          type="button"
          onClick={() => onTap(v.id)}
          disabled={blocked}
          className={`group text-left overflow-hidden transition ${!blocked ? 'cursor-pointer hover:-translate-y-0.5 active:scale-[0.99]' : 'cursor-not-allowed'}`}
          style={{
            background: 'var(--ms-surface)',
            borderRadius: 'var(--ms-radius)',
            boxShadow: 'var(--ms-card-shadow)',
            border: '1px solid var(--ms-border)',
            opacity: !blocked ? 1 : 0.62,
          }}
        >
          <CardMedia v={v} blocked={blocked} few={few} soldOutLabel={soldOutLabel} />
          <div className="p-3.5 sm:p-5">
            <CardText v={v} few={few} soldOutLabel={soldOutLabel} />
            <div className="flex items-center justify-between mt-3 pt-2.5" style={{ borderTop: '1px solid var(--ms-border)' }}>
              <CardPrice v={v} fromLabel={fromLabel} />
              {v.soldOut ? (
                <span className="inline-flex items-center text-[12px] font-black uppercase tracking-wide" style={{ color: 'var(--ms-text-muted)' }}>
                  {soldOutLabel}
                </span>
              ) : v.availableNow ? (
                <span className="inline-flex items-center gap-1 text-[13px] font-bold" style={{ color: 'var(--ms-accent)' }}>
                  {addLabel} <Plus className="w-3.5 h-3.5" />
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[12px] font-bold" style={{ color: 'var(--ms-text-muted)' }}>
                  <Clock className="w-3.5 h-3.5" />{v.schedLabel}
                </span>
              )}
            </div>
          </div>
        </button>
        )
      })}

      {views.length === 0 && (
        <p className="col-span-full text-sm text-center py-8" style={{ color: 'var(--ms-text-muted)' }}>
          {emptyLabel}
        </p>
      )}
    </div>
  )
}

/**
 * Tarjeta de un producto con página (súper online): foto + texto + precio son un link a la
 * página del producto; debajo, «Agregar» sólido a todo el ancho, como `GroceryProductCard`.
 * Lo que pide peso o variante abre su modal desde ese botón (`onTap`, igual que hoy).
 */
export function ProductLinkCard({
  v,
  href,
  blocked,
  few,
  onAdd,
  addLabel,
  soldOutLabel,
  fromLabel,
}: {
  v: MenuItemView
  href: string
  blocked: boolean
  few: boolean
  onAdd: () => void
  addLabel: string
  soldOutLabel: string
  fromLabel?: string
}) {
  return (
    <div
      data-item-id={v.id}
      className={`flex flex-col overflow-hidden transition ${!blocked ? 'hover:-translate-y-0.5' : ''}`}
      style={{
        background: 'var(--ms-surface)',
        borderRadius: 'var(--ms-radius)',
        boxShadow: 'var(--ms-card-shadow)',
        border: '1px solid var(--ms-border)',
        opacity: !blocked ? 1 : 0.62,
      }}
    >
      <a href={href} data-testid="menu-product-link" className="group flex flex-1 flex-col text-left">
        <CardMedia v={v} blocked={blocked} few={few} soldOutLabel={soldOutLabel} />
        <div className="flex flex-1 flex-col p-3.5 sm:p-5 pb-3 sm:pb-3">
          <CardText v={v} few={few} soldOutLabel={soldOutLabel} />
          <div className="mt-auto pt-3">
            <CardPrice v={v} fromLabel={fromLabel} />
          </div>
        </div>
      </a>
      <div className="px-3.5 pb-3.5 sm:px-5 sm:pb-5">
        {v.soldOut ? (
          <span className="block py-2.5 text-center text-[12px] font-black uppercase tracking-wide" style={{ color: 'var(--ms-text-muted)' }}>
            {soldOutLabel}
          </span>
        ) : !v.availableNow ? (
          <span className="flex items-center justify-center gap-1 py-2.5 text-[12px] font-bold" style={{ color: 'var(--ms-text-muted)' }}>
            <Clock className="w-3.5 h-3.5" />{v.schedLabel}
          </span>
        ) : (
          <button
            type="button"
            onClick={onAdd}
            aria-label={`${addLabel} ${v.name}`}
            data-testid="menu-product-add"
            className="flex w-full items-center justify-center gap-1.5 rounded-full py-2.5 text-[14px] font-bold text-white transition hover:opacity-90 active:scale-[0.98]"
            style={{ background: 'var(--ms-accent)' }}
          >
            {addLabel} <Plus className="w-4 h-4" strokeWidth={2.5} />
          </button>
        )}
      </div>
    </div>
  )
}

// Piezas compartidas por las dos tarjetas. Solo son JSX extraído tal cual de la tarjeta de
// main: el `<button>` de un plato pinta exactamente el mismo HTML que antes.

function CardMedia({ v, blocked, few, soldOutLabel }: { v: MenuItemView; blocked: boolean; few: boolean; soldOutLabel: string }) {
  if (!v.imageUrl) return null
  return (
    <div className="relative w-full" style={{ aspectRatio: '16 / 11' }}>
      <Image
        src={v.imageUrl}
        alt={v.name}
        fill
        sizes="(max-width: 640px) 100vw, 33vw"
        className="object-cover"
        style={{ objectPosition: v.focal, filter: !blocked ? undefined : 'grayscale(1)' }}
      />
      {v.badge && (
        <span className="absolute top-3 left-3 flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold text-white shadow-sm" style={{ background: v.badge.color }}>
          <span>{v.badge.icon}</span>{v.badge.label}
        </span>
      )}
      {v.soldOut ? (
        <span
          className="absolute top-3 right-3 inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wide text-white shadow-sm"
          style={{ background: 'rgba(17,17,17,0.82)' }}
        >
          {soldOutLabel}
        </span>
      ) : v.schedLabel && (
        <span
          className="absolute top-3 right-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold text-white shadow-sm"
          style={{ background: v.availableNow ? 'rgba(17,17,17,0.82)' : 'var(--ms-accent)' }}
        >
          <Clock className="w-3 h-3" strokeWidth={2.5} />{v.schedLabel}
        </span>
      )}
      {few && (
        // Abajo a la izquierda: arriba ya están el badge del dueño y el
        // horario/agotado, y sobre la foto es donde más se ve.
        <span className="absolute bottom-3 left-3">
          <LowStockBadge size="lg" />
        </span>
      )}
    </div>
  )
}

function CardText({ v, few, soldOutLabel }: { v: MenuItemView; few: boolean; soldOutLabel: string }) {
  return (
    <>
      {!v.imageUrl && (v.badge || v.schedLabel || v.soldOut || few) && (
        <div className="flex items-center gap-1.5 mb-2 flex-wrap">
          {v.badge && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-white" style={{ background: v.badge.color }}>
              <span>{v.badge.icon}</span>{v.badge.label}
            </span>
          )}
          {v.soldOut ? (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wide text-white"
              style={{ background: 'rgba(17,17,17,0.82)' }}
            >
              {soldOutLabel}
            </span>
          ) : v.schedLabel && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-white"
              style={{ background: v.availableNow ? 'rgba(17,17,17,0.82)' : 'var(--ms-accent)' }}
            >
              <Clock className="w-3 h-3" strokeWidth={2.5} />{v.schedLabel}
            </span>
          )}
          {few && <LowStockBadge size="md" />}
        </div>
      )}
      <h3 className="text-[16px] sm:text-[18px] font-bold leading-tight" style={{ color: 'var(--ms-text)', fontFamily: 'var(--ms-font-display)' }}>
        {v.name}
      </h3>
      {v.description && (
        <p className="text-[13px] mt-1.5 leading-relaxed line-clamp-2" style={{ color: 'var(--ms-text-muted)' }}>
          {v.description}
        </p>
      )}
    </>
  )
}

function CardPrice({ v, fromLabel }: { v: MenuItemView; fromLabel?: string }) {
  return (
    <span className="text-[16px] sm:text-[18px] font-bold" style={{ color: 'var(--ms-text)', fontFamily: 'var(--ms-font-display)' }}>
      {v.priceUnit || v.priceFrom ? (
        // Súper online (R6): «$3.99/lb» o «desde $2.50». Un plato no trae
        // ninguna de las dos claves y pinta la rama de abajo, la de main.
        `${v.priceFrom && fromLabel ? `${fromLabel} ` : ''}$${v.cardPrice.toFixed(2)}${v.priceUnit ? `/${v.priceUnit}` : ''}`
      ) : (
        <>${v.cardPrice.toFixed(2)}</>
      )}
    </span>
  )
}
